import React from 'react';
import {
  Mail,
  Phone,
  MapPin,
  User,
  Briefcase,
  GraduationCap,
  FolderGit2,
  Sparkles,
  Award,
  Globe,
  Target,
  Heart,
  Star,
} from 'lucide-react';

// ==================== 类型定义 ====================

export interface BasicInfo {
  fullName: string;
  email: string;
  phone: string;
  location: string;
  avatar?: string;
  jobTitle?: string;
  workYears?: string;
  summary?: string;
  website?: string;
  linkedin?: string;
  github?: string;
  advantages?: string;
}

export interface Education {
  id: string;
  school: string;
  degree: string;
  major: string;
  startDate: string;
  endDate: string;
  description?: string;
}

export interface WorkExperience {
  id: string;
  company: string;
  position: string;
  startDate: string;
  endDate: string;
  description?: string;
}

export interface Project {
  id: string;
  name: string;
  role: string;
  startDate: string;
  endDate: string;
  techStack?: string;
  achievements?: string;
}

export interface Skills {
  professional?: string;
  languages?: string;
  certificates?: string;
}

export interface ResumeData {
  basicInfo: BasicInfo;
  education: Education[];
  workExperience: WorkExperience[];
  projects: Project[];
  skills: Skills;
}

// ==================== 工具函数 ====================

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'short',
  });
};

const formatDateRange = (start: string, end: string) => {
  const startStr = formatDate(start);
  const endStr = end ? formatDate(end) : '至今';
  return `${startStr} - ${endStr}`;
};

const parseSkills = (skillsStr?: string): string[] => {
  if (!skillsStr) return [];
  return skillsStr
    .split(/[,，、]/)
    .map((s) => s.trim())
    .filter(Boolean);
};

// ==================== 模板1: Elegant - 卡片式优雅设计 ====================

const ElegantTemplate: React.FC<{ data: ResumeData }> = ({ data }) => {
  const { basicInfo, education, workExperience, projects, skills } = data;
  const professionalSkills = parseSkills(skills.professional);

  return (
    <div className="w-[210mm] min-h-[297mm] mx-auto bg-white shadow-lg print:shadow-none font-sans">
      {/* 头部区域 */}
      <div className="bg-gradient-to-br from-teal-600 to-teal-800 text-white p-10">
        <div className="flex items-start gap-6">
          {basicInfo.avatar ? (
            <img
              src={basicInfo.avatar}
              alt={basicInfo.fullName}
              className="w-28 h-28 rounded-full object-cover border-4 border-white/30"
            />
          ) : (
            <div className="w-28 h-28 rounded-full bg-white/20 flex items-center justify-center border-4 border-white/30">
              <User className="w-14 h-14 text-white/80" />
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-4xl font-bold mb-2">{basicInfo.fullName}</h1>
            <p className="text-xl text-teal-100 mb-4">
              {basicInfo.jobTitle}
              {basicInfo.workYears && (
                <span className="ml-2 text-teal-200">
                  · {basicInfo.workYears}
                </span>
              )}
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-teal-100">
              <span className="flex items-center gap-1">
                <Mail className="w-4 h-4" />
                {basicInfo.email}
              </span>
              <span className="flex items-center gap-1">
                <Phone className="w-4 h-4" />
                {basicInfo.phone}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {basicInfo.location}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 内容区域 */}
      <div className="p-10 space-y-8">
        {/* 个人优势 */}
        {basicInfo.advantages && (
          <section>
            <h2 className="text-xl font-bold text-teal-700 mb-4 pb-2 border-b-2 border-teal-100 flex items-center gap-2">
              <Star className="w-5 h-5" />
              个人优势
            </h2>
            <p className="text-gray-600 leading-relaxed whitespace-pre-line">
              {basicInfo.advantages}
            </p>
          </section>
        )}

        {/* 个人简介 */}
        {basicInfo.summary && (
          <section>
            <h2 className="text-xl font-bold text-teal-700 mb-4 pb-2 border-b-2 border-teal-100 flex items-center gap-2">
              <User className="w-5 h-5" />
              个人简介
            </h2>
            <p className="text-gray-600 leading-relaxed">{basicInfo.summary}</p>
          </section>
        )}

        {/* 工作经历 */}
        {workExperience.length > 0 && (
          <section>
            <h2 className="text-xl font-bold text-teal-700 mb-4 pb-2 border-b-2 border-teal-100 flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              工作经历
            </h2>
            <div className="space-y-4">
              {workExperience.map((work) => (
                <div
                  key={work.id}
                  className="bg-gray-50 p-4 rounded-lg border-l-4 border-teal-500"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-bold text-gray-900">
                        {work.company}
                      </h3>
                      <p className="text-teal-600">{work.position}</p>
                    </div>
                    <span className="text-sm text-gray-500 bg-white px-3 py-1 rounded-full">
                      {formatDateRange(work.startDate, work.endDate)}
                    </span>
                  </div>
                  {work.description && (
                    <p className="text-gray-600 text-sm leading-relaxed mt-2 whitespace-pre-line">
                      {work.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 项目经验 */}
        {projects.length > 0 && (
          <section>
            <h2 className="text-xl font-bold text-teal-700 mb-4 pb-2 border-b-2 border-teal-100 flex items-center gap-2">
              <FolderGit2 className="w-5 h-5" />
              项目经验
            </h2>
            <div className="space-y-4">
              {projects.map((proj) => (
                <div
                  key={proj.id}
                  className="bg-gray-50 p-4 rounded-lg border border-gray-200"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-bold text-gray-900">{proj.name}</h3>
                      <p className="text-teal-600 text-sm">{proj.role}</p>
                    </div>
                    <span className="text-sm text-gray-500 bg-white px-3 py-1 rounded-full">
                      {formatDateRange(proj.startDate, proj.endDate)}
                    </span>
                  </div>
                  {proj.techStack && (
                    <p className="text-gray-500 text-sm mt-1">
                      {proj.techStack}
                    </p>
                  )}
                  {proj.achievements && (
                    <p className="text-gray-600 text-sm leading-relaxed mt-2 whitespace-pre-line">
                      {proj.achievements}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 教育背景 */}
        {education.length > 0 && (
          <section>
            <h2 className="text-xl font-bold text-teal-700 mb-4 pb-2 border-b-2 border-teal-100 flex items-center gap-2">
              <GraduationCap className="w-5 h-5" />
              教育背景
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {education.map((edu) => (
                <div
                  key={edu.id}
                  className="bg-gray-50 p-4 rounded-lg border border-gray-200"
                >
                  <h3 className="font-bold text-gray-900">{edu.school}</h3>
                  <p className="text-teal-600 text-sm">
                    {edu.degree} · {edu.major}
                  </p>
                  <p className="text-gray-500 text-sm mt-1">
                    {formatDateRange(edu.startDate, edu.endDate)}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 技能 */}
        {professionalSkills.length > 0 && (
          <section>
            <h2 className="text-xl font-bold text-teal-700 mb-4 pb-2 border-b-2 border-teal-100 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              专业技能
            </h2>
            <div className="flex flex-wrap gap-2">
              {professionalSkills.map((skill, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-teal-50 text-teal-700 rounded-full text-sm font-medium border border-teal-200"
                >
                  {skill}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* 语言与证书 */}
        {(skills.languages || skills.certificates) && (
          <section>
            <h2 className="text-xl font-bold text-teal-700 mb-4 pb-2 border-b-2 border-teal-100 flex items-center gap-2">
              <Award className="w-5 h-5" />
              其他资质
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {skills.languages && (
                <div>
                  <span className="font-medium text-gray-700">语言能力：</span>
                  <span className="text-gray-600">{skills.languages}</span>
                </div>
              )}
              {skills.certificates && (
                <div>
                  <span className="font-medium text-gray-700">证书：</span>
                  <span className="text-gray-600">{skills.certificates}</span>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

// ==================== 模板2: Even - 扁平化现代设计 ====================

const EvenTemplate: React.FC<{ data: ResumeData }> = ({ data }) => {
  const { basicInfo, education, workExperience, projects, skills } = data;
  const professionalSkills = parseSkills(skills.professional);

  return (
    <div className="w-[210mm] min-h-[297mm] mx-auto bg-white shadow-lg print:shadow-none font-sans">
      {/* 简洁头部 */}
      <div className="p-10 pb-6 border-b-2 border-gray-900">
        <div className="flex items-end justify-between">
          <div className="flex items-center gap-6">
            {basicInfo.avatar ? (
              <img
                src={basicInfo.avatar}
                alt={basicInfo.fullName}
                className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gray-100 border-2 border-gray-200 flex items-center justify-center">
                <User className="w-10 h-10 text-gray-400" />
              </div>
            )}
            <div>
              <h1 className="text-5xl font-black text-gray-900 tracking-tight mb-2">
                {basicInfo.fullName}
              </h1>
              <p className="text-xl text-gray-500">
                {basicInfo.jobTitle}
                {basicInfo.workYears && (
                  <span className="ml-2 text-gray-400">
                    · {basicInfo.workYears}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="text-right text-sm text-gray-600 space-y-1">
            <p>{basicInfo.email}</p>
            <p>{basicInfo.phone}</p>
            <p>{basicInfo.location}</p>
          </div>
        </div>
      </div>

      <div className="p-10 space-y-8">
        {/* 个人优势 */}
        {basicInfo.advantages && (
          <section>
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">
              个人优势
            </h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
              {basicInfo.advantages}
            </p>
          </section>
        )}

        {/* 个人简介 */}
        {basicInfo.summary && (
          <section>
            <p className="text-gray-700 leading-relaxed text-lg border-l-4 border-gray-900 pl-4">
              {basicInfo.summary}
            </p>
          </section>
        )}

        {/* 工作经历 */}
        {workExperience.length > 0 && (
          <section>
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">
              工作经历
            </h2>
            <div className="space-y-6">
              {workExperience.map((work) => (
                <div key={work.id} className="grid grid-cols-12 gap-4">
                  <div className="col-span-3">
                    <p className="text-sm text-gray-500">
                      {formatDateRange(work.startDate, work.endDate)}
                    </p>
                  </div>
                  <div className="col-span-9">
                    <h3 className="font-bold text-gray-900 text-lg">
                      {work.company}
                    </h3>
                    <p className="text-gray-600 mb-2">{work.position}</p>
                    {work.description && (
                      <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                        {work.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 项目经验 */}
        {projects.length > 0 && (
          <section>
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">
              项目经验
            </h2>
            <div className="space-y-4">
              {projects.map((proj) => (
                <div key={proj.id} className="grid grid-cols-12 gap-4">
                  <div className="col-span-3">
                    <p className="text-sm text-gray-500">
                      {formatDateRange(proj.startDate, proj.endDate)}
                    </p>
                  </div>
                  <div className="col-span-9">
                    <h3 className="font-bold text-gray-900">{proj.name}</h3>
                    <p className="text-gray-600 text-sm">{proj.role}</p>
                    {proj.techStack && (
                      <p className="text-gray-500 text-xs mt-1">
                        {proj.techStack}
                      </p>
                    )}
                    {proj.achievements && (
                      <p className="text-gray-600 text-sm mt-2 leading-relaxed whitespace-pre-line">
                        {proj.achievements}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 教育背景 */}
        {education.length > 0 && (
          <section>
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">
              教育背景
            </h2>
            <div className="space-y-4">
              {education.map((edu) => (
                <div key={edu.id} className="grid grid-cols-12 gap-4">
                  <div className="col-span-3">
                    <p className="text-sm text-gray-500">
                      {formatDateRange(edu.startDate, edu.endDate)}
                    </p>
                  </div>
                  <div className="col-span-9">
                    <h3 className="font-bold text-gray-900">{edu.school}</h3>
                    <p className="text-gray-600">
                      {edu.degree} · {edu.major}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 技能 */}
        {professionalSkills.length > 0 && (
          <section>
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">
              技能专长
            </h2>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {professionalSkills.map((skill, index) => (
                <span key={index} className="text-gray-700">
                  {skill}
                  {index < professionalSkills.length - 1 && (
                    <span className="text-gray-300 ml-6">|</span>
                  )}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* 语言与证书 */}
        {(skills.languages || skills.certificates) && (
          <section>
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">
              其他资质
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {skills.languages && (
                <div>
                  <span className="font-medium text-gray-700">语言能力：</span>
                  <span className="text-gray-600">{skills.languages}</span>
                </div>
              )}
              {skills.certificates && (
                <div>
                  <span className="font-medium text-gray-700">证书：</span>
                  <span className="text-gray-600">{skills.certificates}</span>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

// ==================== 模板3: Kendall - 专业商务风格 ====================

const KendallTemplate: React.FC<{ data: ResumeData }> = ({ data }) => {
  const { basicInfo, education, workExperience, projects, skills } = data;
  const professionalSkills = parseSkills(skills.professional);

  return (
    <div className="w-[210mm] min-h-[297mm] mx-auto bg-white shadow-lg print:shadow-none font-sans">
      {/* 专业头部 */}
      <div className="bg-slate-900 text-white p-10">
        <div className="flex items-center gap-8">
          {basicInfo.avatar ? (
            <img
              src={basicInfo.avatar}
              alt={basicInfo.fullName}
              className="w-32 h-32 object-cover border-4 border-slate-700"
            />
          ) : (
            <div className="w-32 h-32 bg-slate-800 flex items-center justify-center border-4 border-slate-700">
              <User className="w-16 h-16 text-slate-500" />
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-4xl font-bold mb-2">{basicInfo.fullName}</h1>
            <p className="text-xl text-slate-300 mb-4">
              {basicInfo.jobTitle}
              {basicInfo.workYears && (
                <span className="ml-2 text-slate-400">
                  · {basicInfo.workYears}
                </span>
              )}
            </p>
            <div className="flex flex-wrap gap-6 text-sm text-slate-400">
              <span className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {basicInfo.email}
              </span>
              <span className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                {basicInfo.phone}
              </span>
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {basicInfo.location}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-10 space-y-8">
        {/* 个人优势 */}
        {basicInfo.advantages && (
          <section className="bg-slate-50 p-6 rounded-lg">
            <h2 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
              <Star className="w-5 h-5 text-slate-600" />
              个人优势
            </h2>
            <p className="text-slate-700 leading-relaxed whitespace-pre-line">
              {basicInfo.advantages}
            </p>
          </section>
        )}

        {/* 个人简介 */}
        {basicInfo.summary && (
          <section className="bg-slate-50 p-6 rounded-lg">
            <h2 className="text-lg font-bold text-slate-900 mb-3">职业概述</h2>
            <p className="text-slate-700 leading-relaxed">
              {basicInfo.summary}
            </p>
          </section>
        )}

        {/* 工作经历 */}
        {workExperience.length > 0 && (
          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-4 pb-2 border-b border-slate-300 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-slate-600" />
              工作经历
            </h2>
            <div className="space-y-5">
              {workExperience.map((work) => (
                <div key={work.id}>
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="font-bold text-slate-900">{work.company}</h3>
                    <span className="text-sm text-slate-500">
                      {formatDateRange(work.startDate, work.endDate)}
                    </span>
                  </div>
                  <p className="text-slate-600 font-medium mb-2">
                    {work.position}
                  </p>
                  {work.description && (
                    <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">
                      {work.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 项目经验 */}
        {projects.length > 0 && (
          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-4 pb-2 border-b border-slate-300 flex items-center gap-2">
              <FolderGit2 className="w-5 h-5 text-slate-600" />
              项目经验
            </h2>
            <div className="space-y-5">
              {projects.map((proj) => (
                <div key={proj.id}>
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="font-bold text-slate-900">{proj.name}</h3>
                    <span className="text-sm text-slate-500">
                      {formatDateRange(proj.startDate, proj.endDate)}
                    </span>
                  </div>
                  <p className="text-slate-600 font-medium mb-2">{proj.role}</p>
                  {proj.techStack && (
                    <p className="text-slate-500 text-sm mb-2">
                      {proj.techStack}
                    </p>
                  )}
                  {proj.achievements && (
                    <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">
                      {proj.achievements}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 教育背景 */}
        {education.length > 0 && (
          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-4 pb-2 border-b border-slate-300 flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-slate-600" />
              教育背景
            </h2>
            <div className="space-y-4">
              {education.map((edu) => (
                <div key={edu.id} className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-slate-900">{edu.school}</h3>
                    <p className="text-slate-600">
                      {edu.degree} · {edu.major}
                    </p>
                  </div>
                  <span className="text-sm text-slate-500">
                    {formatDateRange(edu.startDate, edu.endDate)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 技能 */}
        {professionalSkills.length > 0 && (
          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-4 pb-2 border-b border-slate-300 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-slate-600" />
              专业技能
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {professionalSkills.map((skill, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 text-slate-700"
                >
                  <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                  {skill}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 语言与证书 */}
        {(skills.languages || skills.certificates) && (
          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-4 pb-2 border-b border-slate-300 flex items-center gap-2">
              <Award className="w-5 h-5 text-slate-600" />
              其他资质
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {skills.languages && (
                <div>
                  <span className="font-medium text-gray-700">语言能力：</span>
                  <span className="text-gray-600">{skills.languages}</span>
                </div>
              )}
              {skills.certificates && (
                <div>
                  <span className="font-medium text-gray-700">证书：</span>
                  <span className="text-gray-600">{skills.certificates}</span>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

// ==================== 模板4: Caffeine - 创意大胆设计 ====================

const CaffeineTemplate: React.FC<{ data: ResumeData }> = ({ data }) => {
  const { basicInfo, education, workExperience, projects, skills } = data;
  const professionalSkills = parseSkills(skills.professional);

  return (
    <div className="w-[210mm] min-h-[297mm] mx-auto bg-slate-900 text-white shadow-lg print:shadow-none font-sans">
      {/* 创意头部 */}
      <div className="p-10 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500">
        <div className="flex items-center gap-6">
          {basicInfo.avatar ? (
            <img
              src={basicInfo.avatar}
              alt={basicInfo.fullName}
              className="w-28 h-28 rounded-2xl object-cover border-4 border-white/30 rotate-3"
            />
          ) : (
            <div className="w-28 h-28 rounded-2xl bg-white/20 flex items-center justify-center border-4 border-white/30 rotate-3">
              <User className="w-14 h-14 text-white/80" />
            </div>
          )}
          <div>
            <h1 className="text-5xl font-black mb-2 tracking-tight">
              {basicInfo.fullName}
            </h1>
            <p className="text-2xl text-white/90 font-light">
              {basicInfo.jobTitle}
              {basicInfo.workYears && (
                <span className="ml-2 text-white/60">
                  · {basicInfo.workYears}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* 联系信息栏 */}
      <div className="bg-slate-800 px-10 py-4 flex flex-wrap gap-6 text-sm text-slate-300">
        <span className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-pink-400" />
          {basicInfo.email}
        </span>
        <span className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-pink-400" />
          {basicInfo.phone}
        </span>
        <span className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-pink-400" />
          {basicInfo.location}
        </span>
      </div>

      <div className="p-10 space-y-8">
        {/* 个人优势 */}
        {basicInfo.advantages && (
          <section>
            <h2 className="text-2xl font-bold text-pink-400 mb-6 flex items-center gap-2">
              <Star className="w-6 h-6" />
              个人优势
            </h2>
            <p className="text-slate-300 leading-relaxed whitespace-pre-line">
              {basicInfo.advantages}
            </p>
          </section>
        )}

        {/* 个人简介 */}
        {basicInfo.summary && (
          <section>
            <p className="text-slate-300 leading-relaxed text-lg">
              {basicInfo.summary}
            </p>
          </section>
        )}

        {/* 工作经历 */}
        {workExperience.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold text-pink-400 mb-6 flex items-center gap-2">
              <Briefcase className="w-6 h-6" />
              工作经历
            </h2>
            <div className="space-y-6">
              {workExperience.map((work) => (
                <div
                  key={work.id}
                  className="bg-slate-800/50 p-5 rounded-xl border border-slate-700"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-xl text-white">
                      {work.company}
                    </h3>
                    <span className="text-sm text-pink-400 bg-pink-400/10 px-3 py-1 rounded-full">
                      {formatDateRange(work.startDate, work.endDate)}
                    </span>
                  </div>
                  <p className="text-purple-300 mb-3">{work.position}</p>
                  {work.description && (
                    <p className="text-slate-400 text-sm leading-relaxed whitespace-pre-line">
                      {work.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 项目经验 */}
        {projects.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold text-pink-400 mb-6 flex items-center gap-2">
              <FolderGit2 className="w-6 h-6" />
              项目经验
            </h2>
            <div className="space-y-6">
              {projects.map((proj) => (
                <div
                  key={proj.id}
                  className="bg-slate-800/50 p-5 rounded-xl border border-slate-700"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-xl text-white">
                      {proj.name}
                    </h3>
                    <span className="text-sm text-pink-400 bg-pink-400/10 px-3 py-1 rounded-full">
                      {formatDateRange(proj.startDate, proj.endDate)}
                    </span>
                  </div>
                  <p className="text-purple-300 mb-2">{proj.role}</p>
                  {proj.techStack && (
                    <p className="text-slate-500 text-sm mb-2">
                      {proj.techStack}
                    </p>
                  )}
                  {proj.achievements && (
                    <p className="text-slate-400 text-sm leading-relaxed whitespace-pre-line">
                      {proj.achievements}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 教育背景 */}
        {education.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold text-pink-400 mb-6 flex items-center gap-2">
              <GraduationCap className="w-6 h-6" />
              教育背景
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {education.map((edu) => (
                <div
                  key={edu.id}
                  className="bg-slate-800/50 p-4 rounded-xl border border-slate-700"
                >
                  <h3 className="font-bold text-white">{edu.school}</h3>
                  <p className="text-purple-300 text-sm">
                    {edu.degree} · {edu.major}
                  </p>
                  <p className="text-slate-500 text-sm mt-1">
                    {formatDateRange(edu.startDate, edu.endDate)}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 技能 */}
        {professionalSkills.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold text-pink-400 mb-6 flex items-center gap-2">
              <Sparkles className="w-6 h-6" />
              专业技能
            </h2>
            <div className="flex flex-wrap gap-3">
              {professionalSkills.map((skill, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-500 rounded-lg text-sm font-medium"
                >
                  {skill}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* 语言与证书 */}
        {(skills.languages || skills.certificates) && (
          <section>
            <h2 className="text-2xl font-bold text-pink-400 mb-6 flex items-center gap-2">
              <Award className="w-6 h-6" />
              其他资质
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {skills.languages && (
                <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700">
                  <span className="font-medium text-purple-300">
                    语言能力：
                  </span>
                  <span className="text-slate-300">{skills.languages}</span>
                </div>
              )}
              {skills.certificates && (
                <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700">
                  <span className="font-medium text-purple-300">证书：</span>
                  <span className="text-slate-300">{skills.certificates}</span>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

// ==================== 模板5: GradientPro - 渐变专业风 ====================

const GradientProTemplate: React.FC<{ data: ResumeData }> = ({ data }) => {
  const { basicInfo, education, workExperience, projects, skills } = data;
  const professionalSkills = parseSkills(skills.professional);

  return (
    <div className="w-[210mm] min-h-[297mm] mx-auto bg-gradient-to-br from-slate-50 to-blue-50 shadow-lg print:shadow-none font-sans">
      {/* 渐变头部 */}
      <div className="bg-gradient-to-r from-amber-600 via-yellow-600 to-orange-600 text-white p-10">
        <div className="flex items-center gap-6">
          {basicInfo.avatar ? (
            <img
              src={basicInfo.avatar}
              alt={basicInfo.fullName}
              className="w-28 h-28 rounded-2xl object-cover border-4 border-white/30 shadow-xl"
            />
          ) : (
            <div className="w-28 h-28 rounded-2xl bg-white/20 flex items-center justify-center border-4 border-white/30 shadow-xl">
              <User className="w-14 h-14 text-white/80" />
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-4xl font-bold mb-2">{basicInfo.fullName}</h1>
            <p className="text-xl text-blue-100 mb-4">
              {basicInfo.jobTitle}
              {basicInfo.workYears && (
                <span className="ml-2 text-blue-200">
                  · {basicInfo.workYears}
                </span>
              )}
            </p>
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
                <Mail className="w-4 h-4" />
                {basicInfo.email}
              </span>
              <span className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
                <Phone className="w-4 h-4" />
                {basicInfo.phone}
              </span>
              <span className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
                <MapPin className="w-4 h-4" />
                {basicInfo.location}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-10 space-y-8">
        {/* 个人优势 */}
        {basicInfo.advantages && (
          <section className="bg-white p-6 rounded-xl shadow-sm border border-blue-100">
            <h2 className="text-lg font-bold text-amber-700 mb-3 flex items-center gap-2">
              <Star className="w-5 h-5" />
              个人优势
            </h2>
            <p className="text-gray-600 leading-relaxed whitespace-pre-line">
              {basicInfo.advantages}
            </p>
          </section>
        )}

        {/* 个人简介 */}
        {basicInfo.summary && (
          <section className="bg-white p-6 rounded-xl shadow-sm border border-blue-100">
            <h2 className="text-lg font-bold text-amber-700 mb-3 flex items-center gap-2">
              <Target className="w-5 h-5" />
              个人简介
            </h2>
            <p className="text-gray-600 leading-relaxed">{basicInfo.summary}</p>
          </section>
        )}

        {/* 工作经历 */}
        {workExperience.length > 0 && (
          <section>
            <h2 className="text-xl font-bold text-amber-800 mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              工作经历
            </h2>
            <div className="space-y-4">
              {workExperience.map((work) => (
                <div
                  key={work.id}
                  className="bg-white p-5 rounded-xl shadow-sm border-l-4 border-amber-500"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-bold text-gray-900">
                        {work.company}
                      </h3>
                      <p className="text-amber-600">{work.position}</p>
                    </div>
                    <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                      {formatDateRange(work.startDate, work.endDate)}
                    </span>
                  </div>
                  {work.description && (
                    <p className="text-gray-600 text-sm leading-relaxed mt-2 whitespace-pre-line">
                      {work.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 项目经验 */}
        {projects.length > 0 && (
          <section>
            <h2 className="text-xl font-bold text-amber-800 mb-4 flex items-center gap-2">
              <FolderGit2 className="w-5 h-5" />
              项目经验
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {projects.map((proj) => (
                <div
                  key={proj.id}
                  className="bg-white p-4 rounded-xl shadow-sm border border-blue-100"
                >
                  <h3 className="font-bold text-gray-900">{proj.name}</h3>
                  <p className="text-amber-600 text-sm">{proj.role}</p>
                  <p className="text-gray-500 text-xs mt-1">
                    {formatDateRange(proj.startDate, proj.endDate)}
                  </p>
                  {proj.techStack && (
                    <p className="text-gray-500 text-xs mt-1">
                      {proj.techStack}
                    </p>
                  )}
                  {proj.achievements && (
                    <p className="text-gray-600 text-sm mt-2 whitespace-pre-line">
                      {proj.achievements}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 教育背景 */}
        {education.length > 0 && (
          <section>
            <h2 className="text-xl font-bold text-amber-800 mb-4 flex items-center gap-2">
              <GraduationCap className="w-5 h-5" />
              教育背景
            </h2>
            <div className="flex flex-wrap gap-4">
              {education.map((edu) => (
                <div
                  key={edu.id}
                  className="bg-white px-5 py-3 rounded-xl shadow-sm border border-blue-100"
                >
                  <h3 className="font-bold text-gray-900">{edu.school}</h3>
                  <p className="text-amber-600 text-sm">
                    {edu.degree} · {edu.major}
                  </p>
                  <p className="text-gray-500 text-xs">
                    {formatDateRange(edu.startDate, edu.endDate)}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 技能 */}
        {professionalSkills.length > 0 && (
          <section>
            <h2 className="text-xl font-bold text-amber-800 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              专业技能
            </h2>
            <div className="flex flex-wrap gap-2">
              {professionalSkills.map((skill, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-lg text-sm font-medium shadow-sm"
                >
                  {skill}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* 语言与证书 */}
        {(skills.languages || skills.certificates) && (
          <section className="bg-white p-5 rounded-xl shadow-sm border border-blue-100">
            <h2 className="text-lg font-bold text-amber-700 mb-3 flex items-center gap-2">
              <Award className="w-5 h-5" />
              其他资质
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {skills.languages && (
                <div>
                  <span className="font-medium text-gray-700">语言能力：</span>
                  <span className="text-gray-600">{skills.languages}</span>
                </div>
              )}
              {skills.certificates && (
                <div>
                  <span className="font-medium text-gray-700">证书：</span>
                  <span className="text-gray-600">{skills.certificates}</span>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

// ==================== 模板6: Magazine - 杂志排版风 ====================

const MagazineTemplate: React.FC<{ data: ResumeData }> = ({ data }) => {
  const { basicInfo, education, workExperience, projects, skills } = data;
  const professionalSkills = parseSkills(skills.professional);

  return (
    <div className="w-[210mm] min-h-[297mm] mx-auto bg-white shadow-lg print:shadow-none font-serif">
      {/* 杂志风格头部 */}
      <div className="relative">
        <div className="bg-black text-white p-8">
          <div className="flex justify-between items-end">
            <div className="flex-1">
              <p className="text-sm uppercase tracking-[0.3em] text-gray-400 mb-2">
                Professional Resume
              </p>
              <h1 className="text-6xl font-black tracking-tight">
                {basicInfo.fullName}
              </h1>
            </div>
            {basicInfo.avatar ? (
              <img
                src={basicInfo.avatar}
                alt={basicInfo.fullName}
                className="w-24 h-24 object-cover"
              />
            ) : (
              <div className="w-24 h-24 bg-gray-800 flex items-center justify-center">
                <User className="w-12 h-12 text-gray-600" />
              </div>
            )}
          </div>
        </div>
        <div className="bg-gray-100 px-8 py-3 flex justify-between items-center text-sm">
          <span className="font-bold text-black">
            {basicInfo.jobTitle}
            {basicInfo.workYears && (
              <span className="ml-2 text-gray-500">
                · {basicInfo.workYears}
              </span>
            )}
          </span>
          <div className="flex gap-6 text-gray-600">
            <span>{basicInfo.email}</span>
            <span>{basicInfo.phone}</span>
            <span>{basicInfo.location}</span>
          </div>
        </div>
      </div>

      <div className="p-8 grid grid-cols-12 gap-6">
        {/* 左侧主内容 */}
        <div className="col-span-8 space-y-6">
          {/* 个人优势 */}
          {basicInfo.advantages && (
            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3 border-b border-gray-200 pb-1">
                Advantages
              </h2>
              <p className="text-gray-700 leading-relaxed text-sm whitespace-pre-line">
                {basicInfo.advantages}
              </p>
            </section>
          )}

          {/* 个人简介 */}
          {basicInfo.summary && (
            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3 border-b border-gray-200 pb-1">
                About
              </h2>
              <p className="text-gray-700 leading-relaxed text-sm">
                {basicInfo.summary}
              </p>
            </section>
          )}

          {/* 工作经历 */}
          {workExperience.length > 0 && (
            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4 border-b border-gray-200 pb-1">
                Experience
              </h2>
              <div className="space-y-5">
                {workExperience.map((work) => (
                  <div key={work.id}>
                    <div className="flex justify-between items-baseline mb-1">
                      <h3 className="font-bold text-black text-lg">
                        {work.company}
                      </h3>
                      <span className="text-xs text-gray-500">
                        {formatDateRange(work.startDate, work.endDate)}
                      </span>
                    </div>
                    <p className="text-gray-600 italic mb-2">{work.position}</p>
                    {work.description && (
                      <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                        {work.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 项目经验 */}
          {projects.length > 0 && (
            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4 border-b border-gray-200 pb-1">
                Projects
              </h2>
              <div className="space-y-4">
                {projects.map((proj) => (
                  <div key={proj.id}>
                    <div className="flex justify-between items-baseline">
                      <h3 className="font-bold text-black">{proj.name}</h3>
                      <span className="text-xs text-gray-500">
                        {formatDateRange(proj.startDate, proj.endDate)}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm">{proj.role}</p>
                    {proj.techStack && (
                      <p className="text-gray-500 text-xs mt-1">
                        {proj.techStack}
                      </p>
                    )}
                    {proj.achievements && (
                      <p className="text-gray-600 text-sm mt-2 leading-relaxed whitespace-pre-line">
                        {proj.achievements}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* 右侧边栏 */}
        <div className="col-span-4 space-y-6 border-l border-gray-200 pl-6">
          {/* 教育背景 */}
          {education.length > 0 && (
            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4 border-b border-gray-200 pb-1">
                Education
              </h2>
              <div className="space-y-4">
                {education.map((edu) => (
                  <div key={edu.id}>
                    <h3 className="font-bold text-black text-sm">
                      {edu.school}
                    </h3>
                    <p className="text-gray-600 text-xs">
                      {edu.degree} · {edu.major}
                    </p>
                    <p className="text-gray-400 text-xs mt-1">
                      {formatDateRange(edu.startDate, edu.endDate)}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 技能 */}
          {professionalSkills.length > 0 && (
            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4 border-b border-gray-200 pb-1">
                Skills
              </h2>
              <div className="flex flex-wrap gap-2">
                {professionalSkills.map((skill, index) => (
                  <span
                    key={index}
                    className="text-xs bg-black text-white px-2 py-1"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* 语言与证书 */}
          {(skills.languages || skills.certificates) && (
            <section>
              <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4 border-b border-gray-200 pb-1">
                Credentials
              </h2>
              <div className="text-sm space-y-2">
                {skills.languages && (
                  <div>
                    <span className="font-medium text-black text-xs">
                      Languages:
                    </span>
                    <p className="text-gray-600 text-xs">{skills.languages}</p>
                  </div>
                )}
                {skills.certificates && (
                  <div>
                    <span className="font-medium text-black text-xs">
                      Certificates:
                    </span>
                    <p className="text-gray-600 text-xs">
                      {skills.certificates}
                    </p>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== 模板7: Timeline - 时间线风格 ====================

const TimelineTemplate: React.FC<{ data: ResumeData }> = ({ data }) => {
  const { basicInfo, education, workExperience, projects, skills } = data;
  const professionalSkills = parseSkills(skills.professional);

  return (
    <div className="w-[210mm] min-h-[297mm] mx-auto bg-white shadow-lg print:shadow-none font-sans">
      {/* 头部 */}
      <div className="bg-emerald-700 text-white p-8">
        <div className="flex items-center gap-6">
          {basicInfo.avatar ? (
            <img
              src={basicInfo.avatar}
              alt={basicInfo.fullName}
              className="w-24 h-24 rounded-full object-cover border-4 border-white/30"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center border-4 border-white/30">
              <User className="w-12 h-12 text-white/80" />
            </div>
          )}
          <div>
            <h1 className="text-4xl font-bold mb-1">{basicInfo.fullName}</h1>
            <p className="text-emerald-100 text-lg">
              {basicInfo.jobTitle}
              {basicInfo.workYears && (
                <span className="ml-2 text-emerald-200">
                  · {basicInfo.workYears}
                </span>
              )}
            </p>
            <div className="flex gap-4 mt-3 text-sm text-emerald-100">
              <span className="flex items-center gap-1">
                <Mail className="w-4 h-4" />
                {basicInfo.email}
              </span>
              <span className="flex items-center gap-1">
                <Phone className="w-4 h-4" />
                {basicInfo.phone}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {basicInfo.location}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-8">
        {/* 个人优势 */}
        {basicInfo.advantages && (
          <section className="mb-8">
            <h2 className="text-lg font-bold text-emerald-700 mb-4 flex items-center gap-2 justify-center">
              <Star className="w-5 h-5" />
              个人优势
            </h2>
            <p className="text-gray-600 leading-relaxed text-center max-w-2xl mx-auto whitespace-pre-line">
              {basicInfo.advantages}
            </p>
          </section>
        )}

        {/* 个人简介 */}
        {basicInfo.summary && (
          <section className="mb-8">
            <p className="text-gray-600 leading-relaxed text-center max-w-2xl mx-auto">
              {basicInfo.summary}
            </p>
          </section>
        )}

        <div className="grid grid-cols-2 gap-8">
          {/* 左列 - 工作经历 */}
          <div>
            <h2 className="text-lg font-bold text-emerald-700 mb-6 flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              工作经历
            </h2>
            {workExperience.length > 0 && (
              <div className="relative border-l-2 border-emerald-200 ml-3 space-y-6">
                {workExperience.map((work) => (
                  <div key={work.id} className="relative pl-6">
                    <div className="absolute -left-[9px] top-0 w-4 h-4 bg-emerald-500 rounded-full border-4 border-white"></div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <span className="text-xs text-emerald-600 font-medium">
                        {formatDateRange(work.startDate, work.endDate)}
                      </span>
                      <h3 className="font-bold text-gray-900 mt-1">
                        {work.company}
                      </h3>
                      <p className="text-gray-600 text-sm">{work.position}</p>
                      {work.description && (
                        <p className="text-gray-500 text-xs mt-2 leading-relaxed whitespace-pre-line">
                          {work.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 右列 - 教育背景 */}
          <div>
            <h2 className="text-lg font-bold text-emerald-700 mb-6 flex items-center gap-2">
              <GraduationCap className="w-5 h-5" />
              教育背景
            </h2>
            {education.length > 0 && (
              <div className="relative border-l-2 border-emerald-200 ml-3 space-y-6">
                {education.map((edu) => (
                  <div key={edu.id} className="relative pl-6">
                    <div className="absolute -left-[9px] top-0 w-4 h-4 bg-emerald-400 rounded-full border-4 border-white"></div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <span className="text-xs text-emerald-600 font-medium">
                        {formatDateRange(edu.startDate, edu.endDate)}
                      </span>
                      <h3 className="font-bold text-gray-900 mt-1">
                        {edu.school}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {edu.degree} · {edu.major}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 项目经验 */}
            {projects.length > 0 && (
              <>
                <h2 className="text-lg font-bold text-emerald-700 mb-6 mt-8 flex items-center gap-2">
                  <FolderGit2 className="w-5 h-5" />
                  项目经验
                </h2>
                <div className="relative border-l-2 border-emerald-200 ml-3 space-y-6">
                  {projects.map((proj) => (
                    <div key={proj.id} className="relative pl-6">
                      <div className="absolute -left-[9px] top-0 w-4 h-4 bg-emerald-300 rounded-full border-4 border-white"></div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <span className="text-xs text-emerald-600 font-medium">
                          {formatDateRange(proj.startDate, proj.endDate)}
                        </span>
                        <h3 className="font-bold text-gray-900 mt-1">
                          {proj.name}
                        </h3>
                        <p className="text-gray-600 text-sm">{proj.role}</p>
                        {proj.techStack && (
                          <p className="text-gray-500 text-xs mt-1">
                            {proj.techStack}
                          </p>
                        )}
                        {proj.achievements && (
                          <p className="text-gray-600 text-sm mt-2 whitespace-pre-line">
                            {proj.achievements}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* 技能 */}
        {professionalSkills.length > 0 && (
          <section className="mt-8 pt-8 border-t border-gray-200">
            <h2 className="text-lg font-bold text-emerald-700 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              专业技能
            </h2>
            <div className="flex flex-wrap gap-2">
              {professionalSkills.map((skill, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm border border-emerald-200"
                >
                  {skill}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* 语言与证书 */}
        {(skills.languages || skills.certificates) && (
          <section className="mt-6 flex gap-8 text-sm">
            {skills.languages && (
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-emerald-600" />
                <span className="text-gray-600">{skills.languages}</span>
              </div>
            )}
            {skills.certificates && (
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-emerald-600" />
                <span className="text-gray-600">{skills.certificates}</span>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

// ==================== 模板8: Cards - 卡片堆叠风 ====================

const CardsTemplate: React.FC<{ data: ResumeData }> = ({ data }) => {
  const { basicInfo, education, workExperience, projects, skills } = data;
  const professionalSkills = parseSkills(skills.professional);

  return (
    <div className="w-[210mm] min-h-[297mm] mx-auto bg-gradient-to-b from-gray-100 to-gray-200 shadow-lg print:shadow-none font-sans p-8">
      {/* 头部卡片 */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <div className="flex items-center gap-6">
          {basicInfo.avatar ? (
            <img
              src={basicInfo.avatar}
              alt={basicInfo.fullName}
              className="w-24 h-24 rounded-2xl object-cover shadow-md"
            />
          ) : (
            <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center shadow-md">
              <User className="w-12 h-12 text-white" />
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-1">
              {basicInfo.fullName}
            </h1>
            <p className="text-lg text-orange-500 font-medium mb-3">
              {basicInfo.jobTitle}
              {basicInfo.workYears && (
                <span className="ml-2 text-orange-300">
                  · {basicInfo.workYears}
                </span>
              )}
            </p>
            <div className="flex flex-wrap gap-3 text-sm text-gray-600">
              <span className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                <Mail className="w-4 h-4 text-orange-500" />
                {basicInfo.email}
              </span>
              <span className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                <Phone className="w-4 h-4 text-orange-500" />
                {basicInfo.phone}
              </span>
              <span className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-full">
                <MapPin className="w-4 h-4 text-orange-500" />
                {basicInfo.location}
              </span>
            </div>
          </div>
        </div>
        {basicInfo.summary && (
          <p className="mt-4 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
            {basicInfo.summary}
          </p>
        )}
      </div>

      {/* 个人优势卡片 */}
      {basicInfo.advantages && (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Star className="w-4 h-4 text-yellow-500" />
            </div>
            个人优势
          </h2>
          <p className="text-gray-600 leading-relaxed whitespace-pre-line">
            {basicInfo.advantages}
          </p>
        </div>
      )}

      {/* 内容卡片网格 */}
      <div className="grid grid-cols-2 gap-4">
        {/* 工作经历卡片 */}
        {workExperience.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-5">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <Briefcase className="w-4 h-4 text-orange-500" />
              </div>
              工作经历
            </h2>
            <div className="space-y-4">
              {workExperience.map((work) => (
                <div
                  key={work.id}
                  className="border-l-2 border-orange-200 pl-3"
                >
                  <h3 className="font-bold text-gray-900 text-sm">
                    {work.company}
                  </h3>
                  <p className="text-orange-600 text-xs">{work.position}</p>
                  <p className="text-gray-400 text-xs">
                    {formatDateRange(work.startDate, work.endDate)}
                  </p>
                  {work.description && (
                    <p className="text-gray-500 text-xs mt-1 leading-relaxed whitespace-pre-line">
                      {work.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 教育背景卡片 */}
        {education.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-5">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-4 h-4 text-pink-500" />
              </div>
              教育背景
            </h2>
            <div className="space-y-4">
              {education.map((edu) => (
                <div key={edu.id} className="border-l-2 border-pink-200 pl-3">
                  <h3 className="font-bold text-gray-900 text-sm">
                    {edu.school}
                  </h3>
                  <p className="text-pink-600 text-xs">
                    {edu.degree} · {edu.major}
                  </p>
                  <p className="text-gray-400 text-xs">
                    {formatDateRange(edu.startDate, edu.endDate)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 项目经验卡片 */}
        {projects.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-5">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <FolderGit2 className="w-4 h-4 text-purple-500" />
              </div>
              项目经验
            </h2>
            <div className="space-y-4">
              {projects.map((proj) => (
                <div
                  key={proj.id}
                  className="border-l-2 border-purple-200 pl-3"
                >
                  <h3 className="font-bold text-gray-900 text-sm">
                    {proj.name}
                  </h3>
                  <p className="text-purple-600 text-xs">{proj.role}</p>
                  <p className="text-gray-400 text-xs">
                    {formatDateRange(proj.startDate, proj.endDate)}
                  </p>
                  {proj.techStack && (
                    <p className="text-gray-500 text-xs mt-1">
                      {proj.techStack}
                    </p>
                  )}
                  {proj.achievements && (
                    <p className="text-gray-600 text-xs mt-1 whitespace-pre-line">
                      {proj.achievements}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 技能卡片 */}
        {professionalSkills.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-5">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-blue-500" />
              </div>
              技能专长
            </h2>
            <div className="flex flex-wrap gap-2">
              {professionalSkills.map((skill, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-gradient-to-r from-orange-400 to-pink-500 text-white rounded-lg text-xs font-medium"
                >
                  {skill}
                </span>
              ))}
            </div>
            {(skills.languages || skills.certificates) && (
              <div className="mt-4 pt-4 border-t border-gray-100 text-xs space-y-1">
                {skills.languages && (
                  <p className="text-gray-600">
                    <span className="font-medium">语言：</span>
                    {skills.languages}
                  </p>
                )}
                {skills.certificates && (
                  <p className="text-gray-600">
                    <span className="font-medium">证书：</span>
                    {skills.certificates}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== 模板9: SidebarRight - 右侧边栏风 ====================

const SidebarRightTemplate: React.FC<{ data: ResumeData }> = ({ data }) => {
  const { basicInfo, education, workExperience, projects, skills } = data;
  const professionalSkills = parseSkills(skills.professional);

  return (
    <div className="w-[210mm] min-h-[297mm] mx-auto bg-white shadow-lg print:shadow-none font-sans flex">
      {/* 主内容区 */}
      <div className="flex-1 p-8">
        {/* 头部 */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            {basicInfo.fullName}
          </h1>
          <p className="text-xl text-rose-600">
            {basicInfo.jobTitle}
            {basicInfo.workYears && (
              <span className="ml-2 text-rose-400">
                · {basicInfo.workYears}
              </span>
            )}
          </p>
        </div>

        {/* 个人优势 */}
        {basicInfo.advantages && (
          <section className="mb-8">
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-3">
              个人优势
            </h2>
            <p className="text-gray-600 leading-relaxed whitespace-pre-line">
              {basicInfo.advantages}
            </p>
          </section>
        )}

        {/* 个人简介 */}
        {basicInfo.summary && (
          <section className="mb-8">
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-3">
              个人简介
            </h2>
            <p className="text-gray-600 leading-relaxed">{basicInfo.summary}</p>
          </section>
        )}

        {/* 工作经历 */}
        {workExperience.length > 0 && (
          <section className="mb-8">
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">
              工作经历
            </h2>
            <div className="space-y-5">
              {workExperience.map((work) => (
                <div key={work.id}>
                  <div className="flex justify-between items-start mb-1">
                    <h3 className="font-bold text-gray-900">{work.company}</h3>
                    <span className="text-sm text-gray-500">
                      {formatDateRange(work.startDate, work.endDate)}
                    </span>
                  </div>
                  <p className="text-rose-600 mb-2">{work.position}</p>
                  {work.description && (
                    <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                      {work.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 项目经验 */}
        {projects.length > 0 && (
          <section className="mb-8">
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">
              项目经验
            </h2>
            <div className="space-y-4">
              {projects.map((proj) => (
                <div key={proj.id}>
                  <div className="flex justify-between items-start">
                    <h3 className="font-bold text-gray-900">{proj.name}</h3>
                    <span className="text-sm text-gray-500">
                      {formatDateRange(proj.startDate, proj.endDate)}
                    </span>
                  </div>
                  <p className="text-rose-600 text-sm">{proj.role}</p>
                  {proj.techStack && (
                    <p className="text-gray-500 text-xs mt-1">
                      {proj.techStack}
                    </p>
                  )}
                  {proj.achievements && (
                    <p className="text-gray-600 text-sm mt-1 whitespace-pre-line">
                      {proj.achievements}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 教育背景 */}
        {education.length > 0 && (
          <section>
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">
              教育背景
            </h2>
            <div className="space-y-3">
              {education.map((edu) => (
                <div key={edu.id} className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-gray-900">{edu.school}</h3>
                    <p className="text-gray-600 text-sm">
                      {edu.degree} · {edu.major}
                    </p>
                  </div>
                  <span className="text-sm text-gray-500">
                    {formatDateRange(edu.startDate, edu.endDate)}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* 右侧深色边栏 */}
      <div className="w-64 bg-slate-900 text-white p-6">
        {/* 头像 */}
        <div className="mb-6 text-center">
          {basicInfo.avatar ? (
            <img
              src={basicInfo.avatar}
              alt={basicInfo.fullName}
              className="w-28 h-28 rounded-full object-cover mx-auto border-4 border-slate-700"
            />
          ) : (
            <div className="w-28 h-28 rounded-full bg-slate-800 flex items-center justify-center mx-auto border-4 border-slate-700">
              <User className="w-14 h-14 text-slate-600" />
            </div>
          )}
        </div>

        {/* 联系信息 */}
        <div className="mb-8 space-y-3 text-sm">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
            联系方式
          </h3>
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-rose-400" />
            <span className="text-slate-300">{basicInfo.email}</span>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-rose-400" />
            <span className="text-slate-300">{basicInfo.phone}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-rose-400" />
            <span className="text-slate-300">{basicInfo.location}</span>
          </div>
        </div>

        {/* 技能 */}
        {professionalSkills.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
              技能专长
            </h3>
            <div className="flex flex-wrap gap-2">
              {professionalSkills.map((skill, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-slate-800 text-slate-300 text-xs rounded"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* 语言与证书 */}
        {(skills.languages || skills.certificates) && (
          <div>
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
              其他资质
            </h3>
            <div className="space-y-2 text-sm">
              {skills.languages && (
                <div className="flex items-start gap-2">
                  <Globe className="w-4 h-4 text-rose-400 mt-0.5" />
                  <span className="text-slate-300">{skills.languages}</span>
                </div>
              )}
              {skills.certificates && (
                <div className="flex items-start gap-2">
                  <Award className="w-4 h-4 text-rose-400 mt-0.5" />
                  <span className="text-slate-300">{skills.certificates}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== 模板10: MinimalLine - 极简线条风 ====================

const MinimalLineTemplate: React.FC<{ data: ResumeData }> = ({ data }) => {
  const { basicInfo, education, workExperience, projects, skills } = data;
  const professionalSkills = parseSkills(skills.professional);

  return (
    <div className="w-[210mm] min-h-[297mm] mx-auto bg-white shadow-lg print:shadow-none font-sans">
      {/* 极简头部 */}
      <div className="p-12 pb-8">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-5xl font-light text-gray-900 tracking-tight mb-2">
              {basicInfo.fullName}
            </h1>
            <p className="text-gray-500 text-lg">
              {basicInfo.jobTitle}
              {basicInfo.workYears && (
                <span className="ml-2 text-gray-400">
                  · {basicInfo.workYears}
                </span>
              )}
            </p>
          </div>
          {basicInfo.avatar && (
            <img
              src={basicInfo.avatar}
              alt={basicInfo.fullName}
              className="w-20 h-20 object-cover"
            />
          )}
        </div>
        <div className="mt-6 pt-6 border-t border-gray-200 flex gap-8 text-sm text-gray-500">
          <span>{basicInfo.email}</span>
          <span className="text-gray-300">|</span>
          <span>{basicInfo.phone}</span>
          <span className="text-gray-300">|</span>
          <span>{basicInfo.location}</span>
        </div>
      </div>

      <div className="px-12 pb-12 space-y-8">
        {/* 个人优势 */}
        {basicInfo.advantages && (
          <section>
            <h2 className="text-xs font-bold text-gray-900 uppercase tracking-[0.2em] mb-6 pb-2 border-b border-gray-900">
              Advantages
            </h2>
            <p className="text-gray-600 leading-relaxed text-sm max-w-3xl whitespace-pre-line">
              {basicInfo.advantages}
            </p>
          </section>
        )}

        {/* 个人简介 */}
        {basicInfo.summary && (
          <section>
            <p className="text-gray-600 leading-relaxed text-sm max-w-3xl">
              {basicInfo.summary}
            </p>
          </section>
        )}

        {/* 工作经历 */}
        {workExperience.length > 0 && (
          <section>
            <h2 className="text-xs font-bold text-gray-900 uppercase tracking-[0.2em] mb-6 pb-2 border-b border-gray-900">
              Experience
            </h2>
            <div className="space-y-6">
              {workExperience.map((work) => (
                <div key={work.id} className="grid grid-cols-12 gap-4">
                  <div className="col-span-3">
                    <p className="text-xs text-gray-400">
                      {formatDateRange(work.startDate, work.endDate)}
                    </p>
                  </div>
                  <div className="col-span-9">
                    <h3 className="font-medium text-gray-900">
                      {work.company}
                    </h3>
                    <p className="text-gray-500 text-sm mb-2">
                      {work.position}
                    </p>
                    {work.description && (
                      <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                        {work.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 项目经验 */}
        {projects.length > 0 && (
          <section>
            <h2 className="text-xs font-bold text-gray-900 uppercase tracking-[0.2em] mb-6 pb-2 border-b border-gray-900">
              Projects
            </h2>
            <div className="space-y-4">
              {projects.map((proj) => (
                <div key={proj.id} className="grid grid-cols-12 gap-4">
                  <div className="col-span-3">
                    <p className="text-xs text-gray-400">
                      {formatDateRange(proj.startDate, proj.endDate)}
                    </p>
                  </div>
                  <div className="col-span-9">
                    <h3 className="font-medium text-gray-900">{proj.name}</h3>
                    <p className="text-gray-500 text-sm">{proj.role}</p>
                    {proj.techStack && (
                      <p className="text-gray-400 text-xs mt-1">
                        {proj.techStack}
                      </p>
                    )}
                    {proj.achievements && (
                      <p className="text-gray-600 text-sm mt-1 whitespace-pre-line">
                        {proj.achievements}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 教育背景 */}
        {education.length > 0 && (
          <section>
            <h2 className="text-xs font-bold text-gray-900 uppercase tracking-[0.2em] mb-6 pb-2 border-b border-gray-900">
              Education
            </h2>
            <div className="space-y-4">
              {education.map((edu) => (
                <div key={edu.id} className="grid grid-cols-12 gap-4">
                  <div className="col-span-3">
                    <p className="text-xs text-gray-400">
                      {formatDateRange(edu.startDate, edu.endDate)}
                    </p>
                  </div>
                  <div className="col-span-9">
                    <h3 className="font-medium text-gray-900">{edu.school}</h3>
                    <p className="text-gray-500 text-sm">
                      {edu.degree} · {edu.major}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 技能 */}
        {professionalSkills.length > 0 && (
          <section>
            <h2 className="text-xs font-bold text-gray-900 uppercase tracking-[0.2em] mb-6 pb-2 border-b border-gray-900">
              Skills
            </h2>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600">
              {professionalSkills.map((skill, index) => (
                <span key={index}>{skill}</span>
              ))}
            </div>
          </section>
        )}

        {/* 语言与证书 */}
        {(skills.languages || skills.certificates) && (
          <section>
            <h2 className="text-xs font-bold text-gray-900 uppercase tracking-[0.2em] mb-6 pb-2 border-b border-gray-900">
              Credentials
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
              {skills.languages && (
                <div>
                  <span className="text-gray-400 text-xs block mb-1">
                    Languages
                  </span>
                  {skills.languages}
                </div>
              )}
              {skills.certificates && (
                <div>
                  <span className="text-gray-400 text-xs block mb-1">
                    Certificates
                  </span>
                  {skills.certificates}
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

// ==================== 模板11: ColorBlock - 色块分区风 ====================

const ColorBlockTemplate: React.FC<{ data: ResumeData }> = ({ data }) => {
  const { basicInfo, education, workExperience, projects, skills } = data;
  const professionalSkills = parseSkills(skills.professional);

  return (
    <div className="w-[210mm] min-h-[297mm] mx-auto bg-white shadow-lg print:shadow-none font-sans">
      {/* 彩色头部 */}
      <div className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-300 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-300 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        <div className="relative p-10">
          <div className="flex items-center gap-6">
            {basicInfo.avatar ? (
              <img
                src={basicInfo.avatar}
                alt={basicInfo.fullName}
                className="w-28 h-28 rounded-full object-cover border-4 border-white shadow-lg"
              />
            ) : (
              <div className="w-28 h-28 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center border-4 border-white shadow-lg">
                <User className="w-14 h-14 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                {basicInfo.fullName}
              </h1>
              <p className="text-xl text-cyan-600">
                {basicInfo.jobTitle}
                {basicInfo.workYears && (
                  <span className="ml-2 text-cyan-400">
                    · {basicInfo.workYears}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="mt-6 flex gap-6 text-sm text-gray-600">
            <span className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full">
              <Mail className="w-4 h-4 text-cyan-600" />
              {basicInfo.email}
            </span>
            <span className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full">
              <Phone className="w-4 h-4 text-cyan-600" />
              {basicInfo.phone}
            </span>
            <span className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full">
              <MapPin className="w-4 h-4 text-cyan-600" />
              {basicInfo.location}
            </span>
          </div>
        </div>
      </div>

      <div className="p-10 space-y-6">
        {/* 个人优势 */}
        {basicInfo.advantages && (
          <section className="bg-yellow-50 p-6 rounded-2xl border-l-4 border-yellow-400">
            <h2 className="text-lg font-bold text-yellow-800 mb-3 flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              个人优势
            </h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
              {basicInfo.advantages}
            </p>
          </section>
        )}

        {/* 个人简介 */}
        {basicInfo.summary && (
          <section className="bg-yellow-50 p-6 rounded-2xl border-l-4 border-yellow-400">
            <p className="text-gray-700 leading-relaxed">{basicInfo.summary}</p>
          </section>
        )}

        <div className="grid grid-cols-2 gap-6">
          {/* 工作经历 */}
          {workExperience.length > 0 && (
            <section className="bg-cyan-50 p-6 rounded-2xl">
              <h2 className="text-lg font-bold text-cyan-800 mb-4 flex items-center gap-2">
                <div className="w-3 h-3 bg-cyan-500 rounded-full"></div>
                工作经历
              </h2>
              <div className="space-y-4">
                {workExperience.map((work) => (
                  <div key={work.id}>
                    <h3 className="font-bold text-gray-900">{work.company}</h3>
                    <p className="text-cyan-700 text-sm">{work.position}</p>
                    <p className="text-gray-500 text-xs">
                      {formatDateRange(work.startDate, work.endDate)}
                    </p>
                    {work.description && (
                      <p className="text-gray-600 text-xs mt-1 leading-relaxed whitespace-pre-line">
                        {work.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 教育背景 */}
          {education.length > 0 && (
            <section className="bg-pink-50 p-6 rounded-2xl">
              <h2 className="text-lg font-bold text-pink-800 mb-4 flex items-center gap-2">
                <div className="w-3 h-3 bg-pink-500 rounded-full"></div>
                教育背景
              </h2>
              <div className="space-y-4">
                {education.map((edu) => (
                  <div key={edu.id}>
                    <h3 className="font-bold text-gray-900">{edu.school}</h3>
                    <p className="text-pink-700 text-sm">
                      {edu.degree} · {edu.major}
                    </p>
                    <p className="text-gray-500 text-xs">
                      {formatDateRange(edu.startDate, edu.endDate)}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 项目经验 */}
          {projects.length > 0 && (
            <section className="bg-purple-50 p-6 rounded-2xl">
              <h2 className="text-lg font-bold text-purple-800 mb-4 flex items-center gap-2">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                项目经验
              </h2>
              <div className="space-y-4">
                {projects.map((proj) => (
                  <div key={proj.id}>
                    <h3 className="font-bold text-gray-900">{proj.name}</h3>
                    <p className="text-purple-700 text-sm">{proj.role}</p>
                    <p className="text-gray-500 text-xs">
                      {formatDateRange(proj.startDate, proj.endDate)}
                    </p>
                    {proj.techStack && (
                      <p className="text-gray-500 text-xs mt-1">
                        {proj.techStack}
                      </p>
                    )}
                    {proj.achievements && (
                      <p className="text-gray-600 text-sm mt-1 whitespace-pre-line">
                        {proj.achievements}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 技能 */}
          {professionalSkills.length > 0 && (
            <section className="bg-green-50 p-6 rounded-2xl">
              <h2 className="text-lg font-bold text-green-800 mb-4 flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                技能专长
              </h2>
              <div className="flex flex-wrap gap-2">
                {professionalSkills.map((skill, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-white text-green-700 rounded-full text-sm shadow-sm"
                  >
                    {skill}
                  </span>
                ))}
              </div>
              {(skills.languages || skills.certificates) && (
                <div className="mt-4 pt-4 border-t border-green-200 text-sm space-y-1">
                  {skills.languages && (
                    <p className="text-green-800">
                      <span className="font-medium">语言：</span>
                      <span className="text-green-700">{skills.languages}</span>
                    </p>
                  )}
                  {skills.certificates && (
                    <p className="text-green-800">
                      <span className="font-medium">证书：</span>
                      <span className="text-green-700">
                        {skills.certificates}
                      </span>
                    </p>
                  )}
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== 模板12: Typography - 字体排版风 ====================

const TypographyTemplate: React.FC<{ data: ResumeData }> = ({ data }) => {
  const { basicInfo, education, workExperience, projects, skills } = data;
  const professionalSkills = parseSkills(skills.professional);

  return (
    <div className="w-[210mm] min-h-[297mm] mx-auto bg-white shadow-lg print:shadow-none">
      {/* 超大姓名头部 */}
      <div className="p-10 border-b-8 border-black">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-sm uppercase tracking-[0.5em] text-gray-400 mb-2">
              Resume
            </p>
            <h1 className="text-7xl font-black text-black tracking-tighter leading-none">
              {basicInfo.fullName}
            </h1>
          </div>
          {basicInfo.avatar && (
            <img
              src={basicInfo.avatar}
              alt={basicInfo.fullName}
              className="w-24 h-24 object-cover"
            />
          )}
        </div>
        <p className="text-2xl text-gray-600 mt-4 font-light">
          {basicInfo.jobTitle}
          {basicInfo.workYears && (
            <span className="ml-2 text-gray-400">· {basicInfo.workYears}</span>
          )}
        </p>
      </div>

      {/* 联系信息 */}
      <div className="bg-black text-white px-10 py-4 flex justify-between items-center text-sm">
        <span>{basicInfo.email}</span>
        <span>{basicInfo.phone}</span>
        <span>{basicInfo.location}</span>
      </div>

      <div className="p-10 space-y-8">
        {/* 个人优势 */}
        {basicInfo.advantages && (
          <section>
            <h2 className="text-4xl font-black text-black mb-6 tracking-tight">
              Advantages
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed font-light whitespace-pre-line">
              {basicInfo.advantages}
            </p>
          </section>
        )}

        {/* 个人简介 */}
        {basicInfo.summary && (
          <section>
            <p className="text-xl text-gray-700 leading-relaxed font-light">
              {basicInfo.summary}
            </p>
          </section>
        )}

        {/* 工作经历 */}
        {workExperience.length > 0 && (
          <section>
            <h2 className="text-4xl font-black text-black mb-6 tracking-tight">
              Experience
            </h2>
            <div className="space-y-6">
              {workExperience.map((work) => (
                <div key={work.id} className="border-l-4 border-black pl-6">
                  <div className="flex justify-between items-baseline mb-2">
                    <h3 className="text-2xl font-bold text-black">
                      {work.company}
                    </h3>
                    <span className="text-sm text-gray-500 font-mono">
                      {formatDateRange(work.startDate, work.endDate)}
                    </span>
                  </div>
                  <p className="text-lg text-gray-600 mb-2">{work.position}</p>
                  {work.description && (
                    <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                      {work.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 项目经验 */}
        {projects.length > 0 && (
          <section>
            <h2 className="text-4xl font-black text-black mb-6 tracking-tight">
              Projects
            </h2>
            <div className="space-y-4">
              {projects.map((proj) => (
                <div key={proj.id} className="border-l-4 border-gray-300 pl-6">
                  <div className="flex justify-between items-baseline">
                    <h3 className="text-xl font-bold text-black">
                      {proj.name}
                    </h3>
                    <span className="text-sm text-gray-500 font-mono">
                      {formatDateRange(proj.startDate, proj.endDate)}
                    </span>
                  </div>
                  <p className="text-gray-600">{proj.role}</p>
                  {proj.techStack && (
                    <p className="text-gray-400 text-sm mt-1">
                      {proj.techStack}
                    </p>
                  )}
                  {proj.achievements && (
                    <p className="text-gray-700 text-sm mt-2 whitespace-pre-line">
                      {proj.achievements}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 教育背景 */}
        {education.length > 0 && (
          <section>
            <h2 className="text-4xl font-black text-black mb-6 tracking-tight">
              Education
            </h2>
            <div className="space-y-4">
              {education.map((edu) => (
                <div key={edu.id} className="border-l-4 border-gray-300 pl-6">
                  <div className="flex justify-between items-baseline">
                    <h3 className="text-xl font-bold text-black">
                      {edu.school}
                    </h3>
                    <span className="text-sm text-gray-500 font-mono">
                      {formatDateRange(edu.startDate, edu.endDate)}
                    </span>
                  </div>
                  <p className="text-gray-600">
                    {edu.degree} · {edu.major}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 技能 */}
        {professionalSkills.length > 0 && (
          <section>
            <h2 className="text-4xl font-black text-black mb-6 tracking-tight">
              Skills
            </h2>
            <div className="flex flex-wrap gap-4">
              {professionalSkills.map((skill, index) => (
                <span
                  key={index}
                  className="text-xl font-bold text-black border-b-2 border-black pb-1"
                >
                  {skill}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* 语言与证书 */}
        {(skills.languages || skills.certificates) && (
          <section>
            <h2 className="text-4xl font-black text-black mb-6 tracking-tight">
              Credentials
            </h2>
            <div className="grid grid-cols-2 gap-6">
              {skills.languages && (
                <div>
                  <p className="text-sm text-gray-400 uppercase tracking-widest mb-1">
                    Languages
                  </p>
                  <p className="text-lg text-gray-700">{skills.languages}</p>
                </div>
              )}
              {skills.certificates && (
                <div>
                  <p className="text-sm text-gray-400 uppercase tracking-widest mb-1">
                    Certificates
                  </p>
                  <p className="text-lg text-gray-700">{skills.certificates}</p>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

// ==================== 模板13: PhotoHeader - 照片头部风 ====================

const PhotoHeaderTemplate: React.FC<{ data: ResumeData }> = ({ data }) => {
  const { basicInfo, education, workExperience, projects, skills } = data;
  const professionalSkills = parseSkills(skills.professional);

  return (
    <div className="w-[210mm] min-h-[297mm] mx-auto bg-white shadow-lg print:shadow-none font-sans">
      {/* 全宽背景头部 */}
      <div className="relative h-48 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-600">
        <div className="absolute -bottom-16 left-10">
          {basicInfo.avatar ? (
            <img
              src={basicInfo.avatar}
              alt={basicInfo.fullName}
              className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-xl"
            />
          ) : (
            <div className="w-32 h-32 rounded-full bg-white flex items-center justify-center border-4 border-white shadow-xl">
              <User className="w-16 h-16 text-slate-400" />
            </div>
          )}
        </div>
      </div>

      {/* 头部信息区 */}
      <div className="pt-20 pb-6 px-10 bg-gray-50">
        <div className="flex justify-between items-end">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              {basicInfo.fullName}
            </h1>
            <p className="text-xl text-slate-600">
              {basicInfo.jobTitle}
              {basicInfo.workYears && (
                <span className="ml-2 text-slate-400">
                  · {basicInfo.workYears}
                </span>
              )}
            </p>
          </div>
          <div className="text-right text-sm text-gray-600 space-y-1">
            <p className="flex items-center justify-end gap-2">
              {basicInfo.email}
              <Mail className="w-4 h-4 text-slate-500" />
            </p>
            <p className="flex items-center justify-end gap-2">
              {basicInfo.phone}
              <Phone className="w-4 h-4 text-slate-500" />
            </p>
            <p className="flex items-center justify-end gap-2">
              {basicInfo.location}
              <MapPin className="w-4 h-4 text-slate-500" />
            </p>
          </div>
        </div>
      </div>

      <div className="p-10 space-y-8">
        {/* 个人优势 */}
        {basicInfo.advantages && (
          <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Star className="w-5 h-5 text-rose-500" />
              个人优势
            </h2>
            <p className="text-gray-600 leading-relaxed whitespace-pre-line">
              {basicInfo.advantages}
            </p>
          </section>
        )}

        {/* 个人简介 */}
        {basicInfo.summary && (
          <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Heart className="w-5 h-5 text-rose-500" />
              个人简介
            </h2>
            <p className="text-gray-600 leading-relaxed">{basicInfo.summary}</p>
          </section>
        )}

        <div className="grid grid-cols-2 gap-8">
          {/* 左列 */}
          <div className="space-y-6">
            {/* 工作经历 */}
            {workExperience.length > 0 && (
              <section>
                <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-slate-600" />
                  工作经历
                </h2>
                <div className="space-y-4">
                  {workExperience.map((work) => (
                    <div
                      key={work.id}
                      className="border-l-2 border-slate-300 pl-4"
                    >
                      <h3 className="font-bold text-gray-900">
                        {work.company}
                      </h3>
                      <p className="text-slate-600 text-sm">{work.position}</p>
                      <p className="text-gray-400 text-xs">
                        {formatDateRange(work.startDate, work.endDate)}
                      </p>
                      {work.description && (
                        <p className="text-gray-600 text-sm mt-1 leading-relaxed whitespace-pre-line">
                          {work.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* 项目经验 */}
            {projects.length > 0 && (
              <section>
                <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <FolderGit2 className="w-5 h-5 text-slate-600" />
                  项目经验
                </h2>
                <div className="space-y-4">
                  {projects.map((proj) => (
                    <div
                      key={proj.id}
                      className="border-l-2 border-slate-300 pl-4"
                    >
                      <h3 className="font-bold text-gray-900">{proj.name}</h3>
                      <p className="text-slate-600 text-sm">{proj.role}</p>
                      <p className="text-gray-400 text-xs">
                        {formatDateRange(proj.startDate, proj.endDate)}
                      </p>
                      {proj.techStack && (
                        <p className="text-gray-500 text-xs mt-1">
                          {proj.techStack}
                        </p>
                      )}
                      {proj.achievements && (
                        <p className="text-gray-600 text-sm mt-1 whitespace-pre-line">
                          {proj.achievements}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* 右列 */}
          <div className="space-y-6">
            {/* 教育背景 */}
            {education.length > 0 && (
              <section>
                <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-slate-600" />
                  教育背景
                </h2>
                <div className="space-y-3">
                  {education.map((edu) => (
                    <div key={edu.id} className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-bold text-gray-900">{edu.school}</h3>
                      <p className="text-slate-600 text-sm">
                        {edu.degree} · {edu.major}
                      </p>
                      <p className="text-gray-400 text-xs">
                        {formatDateRange(edu.startDate, edu.endDate)}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* 技能 */}
            {professionalSkills.length > 0 && (
              <section>
                <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-slate-600" />
                  技能专长
                </h2>
                <div className="flex flex-wrap gap-2">
                  {professionalSkills.map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* 语言与证书 */}
            {(skills.languages || skills.certificates) && (
              <section>
                <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-slate-600" />
                  其他资质
                </h2>
                <div className="space-y-2 text-sm">
                  {skills.languages && (
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-slate-400" />
                      <span className="text-gray-600">{skills.languages}</span>
                    </div>
                  )}
                  {skills.certificates && (
                    <div className="flex items-center gap-2">
                      <Award className="w-4 h-4 text-slate-400" />
                      <span className="text-gray-600">
                        {skills.certificates}
                      </span>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== 模板14: TwoColumn - 双栏紧凑风 ====================

const TwoColumnTemplate: React.FC<{ data: ResumeData }> = ({ data }) => {
  const { basicInfo, education, workExperience, projects, skills } = data;
  const professionalSkills = parseSkills(skills.professional);

  return (
    <div className="w-[210mm] min-h-[297mm] mx-auto bg-white shadow-lg print:shadow-none font-sans">
      {/* 紧凑头部 */}
      <div className="bg-slate-800 text-white p-6">
        <div className="flex items-center gap-4">
          {basicInfo.avatar ? (
            <img
              src={basicInfo.avatar}
              alt={basicInfo.fullName}
              className="w-20 h-20 rounded-lg object-cover"
            />
          ) : (
            <div className="w-20 h-20 rounded-lg bg-slate-700 flex items-center justify-center">
              <User className="w-10 h-10 text-slate-500" />
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-3xl font-bold">{basicInfo.fullName}</h1>
            <p className="text-slate-300">
              {basicInfo.jobTitle}
              {basicInfo.workYears && (
                <span className="ml-2 text-slate-400">
                  · {basicInfo.workYears}
                </span>
              )}
            </p>
          </div>
          <div className="text-right text-sm text-slate-300 space-y-1">
            <p>{basicInfo.email}</p>
            <p>{basicInfo.phone}</p>
            <p>{basicInfo.location}</p>
          </div>
        </div>
      </div>

      <div className="p-6 grid grid-cols-2 gap-6">
        {/* 左栏 */}
        <div className="space-y-5">
          {/* 个人优势 */}
          {basicInfo.advantages && (
            <section>
              <h2 className="text-base font-bold mb-3 flex items-center gap-2 text-slate-800 bg-slate-100 px-3 py-2 rounded">
                <Star className="w-4 h-4" />
                个人优势
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                {basicInfo.advantages}
              </p>
            </section>
          )}

          {/* 个人简介 */}
          {basicInfo.summary && (
            <section>
              <p className="text-gray-600 text-sm leading-relaxed">
                {basicInfo.summary}
              </p>
            </section>
          )}

          {/* 工作经历 */}
          {workExperience.length > 0 && (
            <section>
              <h2 className="text-base font-bold mb-3 flex items-center gap-2 text-slate-800 bg-slate-100 px-3 py-2 rounded">
                <Briefcase className="w-4 h-4" />
                工作经历
              </h2>
              <div className="space-y-3">
                {workExperience.map((work) => (
                  <div key={work.id} className="text-sm">
                    <div className="flex justify-between items-start">
                      <h3 className="font-bold text-gray-900">
                        {work.company}
                      </h3>
                      <span className="text-xs text-slate-500">
                        {formatDateRange(work.startDate, work.endDate)}
                      </span>
                    </div>
                    <p className="text-slate-600">{work.position}</p>
                    {work.description && (
                      <p className="text-gray-600 text-xs leading-relaxed mt-1 whitespace-pre-line">
                        {work.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 教育背景 */}
          {education.length > 0 && (
            <section>
              <h2 className="text-base font-bold mb-3 flex items-center gap-2 text-slate-800 bg-slate-100 px-3 py-2 rounded">
                <GraduationCap className="w-4 h-4" />
                教育背景
              </h2>
              <div className="space-y-2">
                {education.map((edu) => (
                  <div
                    key={edu.id}
                    className="flex justify-between items-start text-sm"
                  >
                    <div>
                      <h3 className="font-bold text-gray-900">{edu.school}</h3>
                      <p className="text-gray-600 text-xs">
                        {edu.degree} · {edu.major}
                      </p>
                    </div>
                    <span className="text-xs text-slate-500">
                      {formatDateRange(edu.startDate, edu.endDate)}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* 右栏 */}
        <div className="space-y-5">
          {/* 项目经验 */}
          {projects.length > 0 && (
            <section>
              <h2 className="text-base font-bold mb-3 flex items-center gap-2 text-slate-800 bg-slate-100 px-3 py-2 rounded">
                <FolderGit2 className="w-4 h-4" />
                项目经验
              </h2>
              <div className="space-y-3">
                {projects.map((proj) => (
                  <div
                    key={proj.id}
                    className="text-sm border-l-2 border-slate-300 pl-3"
                  >
                    <div className="flex justify-between items-start">
                      <h3 className="font-bold text-gray-900">{proj.name}</h3>
                      <span className="text-xs text-slate-500">
                        {formatDateRange(proj.startDate, proj.endDate)}
                      </span>
                    </div>
                    <p className="text-slate-600">{proj.role}</p>
                    {proj.techStack && (
                      <p className="text-xs text-slate-500 mt-1">
                        {proj.techStack}
                      </p>
                    )}
                    {proj.achievements && (
                      <p className="text-gray-600 text-xs leading-relaxed mt-1 whitespace-pre-line">
                        {proj.achievements}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 技能 */}
          {professionalSkills.length > 0 && (
            <section>
              <h2 className="text-base font-bold mb-3 flex items-center gap-2 text-slate-800 bg-slate-100 px-3 py-2 rounded">
                <Sparkles className="w-4 h-4" />
                技能专长
              </h2>
              <div className="flex flex-wrap gap-1.5">
                {professionalSkills.map((skill, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* 语言与证书 */}
          {(skills.languages || skills.certificates) && (
            <section>
              <h2 className="text-base font-bold mb-3 flex items-center gap-2 text-slate-800 bg-slate-100 px-3 py-2 rounded">
                <Award className="w-4 h-4" />
                其他资质
              </h2>
              <div className="text-sm space-y-1">
                {skills.languages && (
                  <div className="flex gap-2">
                    <span className="font-medium text-gray-700">语言：</span>
                    <span className="text-gray-600">{skills.languages}</span>
                  </div>
                )}
                {skills.certificates && (
                  <div className="flex gap-2">
                    <span className="font-medium text-gray-700">证书：</span>
                    <span className="text-gray-600">{skills.certificates}</span>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== 模板15: ModernBox - 现代方框风 ====================

const ModernBoxTemplate: React.FC<{ data: ResumeData }> = ({ data }) => {
  const { basicInfo, education, workExperience, projects, skills } = data;
  const professionalSkills = parseSkills(skills.professional);

  return (
    <div className="w-[210mm] min-h-[297mm] mx-auto bg-gray-100 shadow-lg print:shadow-none font-sans p-6">
      {/* 工业风格头部 */}
      <div className="bg-white p-6 mb-4 border-l-4 border-gray-900">
        <div className="flex items-center gap-6">
          {basicInfo.avatar ? (
            <img
              src={basicInfo.avatar}
              alt={basicInfo.fullName}
              className="w-24 h-24 object-cover border-2 border-gray-900"
            />
          ) : (
            <div className="w-24 h-24 bg-gray-200 flex items-center justify-center border-2 border-gray-900">
              <User className="w-12 h-12 text-gray-500" />
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-4xl font-black text-gray-900 tracking-tight uppercase">
              {basicInfo.fullName}
            </h1>
            <p className="text-lg text-gray-600 mt-1 font-mono">
              {basicInfo.jobTitle}
              {basicInfo.workYears && (
                <span className="ml-2 text-gray-400">
                  · {basicInfo.workYears}
                </span>
              )}
            </p>
            <div className="flex gap-6 mt-3 text-sm font-mono text-gray-500">
              <span>{basicInfo.email}</span>
              <span>{basicInfo.phone}</span>
              <span>{basicInfo.location}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 个人优势框 */}
      {basicInfo.advantages && (
        <div className="bg-white p-5 mb-4 border border-gray-300">
          <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest mb-3 pb-2 border-b-2 border-gray-900">
            Advantages
          </h2>
          <p className="text-gray-700 leading-relaxed text-sm whitespace-pre-line">
            {basicInfo.advantages}
          </p>
        </div>
      )}

      {/* 简介框 */}
      {basicInfo.summary && (
        <div className="bg-white p-5 mb-4 border border-gray-300">
          <p className="text-gray-700 leading-relaxed text-sm">
            {basicInfo.summary}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {/* 工作经历 */}
        {workExperience.length > 0 && (
          <div className="bg-white p-5 border border-gray-300">
            <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest mb-4 pb-2 border-b-2 border-gray-900">
              Experience
            </h2>
            <div className="space-y-4">
              {workExperience.map((work) => (
                <div key={work.id} className="border-l-2 border-gray-300 pl-3">
                  <div className="flex justify-between items-start">
                    <h3 className="font-bold text-gray-900 text-sm">
                      {work.company}
                    </h3>
                    <span className="text-xs font-mono text-gray-500">
                      {formatDateRange(work.startDate, work.endDate)}
                    </span>
                  </div>
                  <p className="text-gray-600 text-xs">{work.position}</p>
                  {work.description && (
                    <p className="text-gray-500 text-xs mt-1 leading-relaxed whitespace-pre-line">
                      {work.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 教育背景 */}
        {education.length > 0 && (
          <div className="bg-white p-5 border border-gray-300">
            <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest mb-4 pb-2 border-b-2 border-gray-900">
              Education
            </h2>
            <div className="space-y-4">
              {education.map((edu) => (
                <div key={edu.id} className="border-l-2 border-gray-300 pl-3">
                  <div className="flex justify-between items-start">
                    <h3 className="font-bold text-gray-900 text-sm">
                      {edu.school}
                    </h3>
                    <span className="text-xs font-mono text-gray-500">
                      {formatDateRange(edu.startDate, edu.endDate)}
                    </span>
                  </div>
                  <p className="text-gray-600 text-xs">
                    {edu.degree} · {edu.major}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 项目经验 */}
        {projects.length > 0 && (
          <div className="bg-white p-5 border border-gray-300">
            <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest mb-4 pb-2 border-b-2 border-gray-900">
              Projects
            </h2>
            <div className="space-y-4">
              {projects.map((proj) => (
                <div key={proj.id} className="border-l-2 border-gray-300 pl-3">
                  <div className="flex justify-between items-start">
                    <h3 className="font-bold text-gray-900 text-sm">
                      {proj.name}
                    </h3>
                    <span className="text-xs font-mono text-gray-500">
                      {formatDateRange(proj.startDate, proj.endDate)}
                    </span>
                  </div>
                  <p className="text-gray-600 text-xs">{proj.role}</p>
                  {proj.techStack && (
                    <p className="text-gray-500 text-xs mt-1">
                      {proj.techStack}
                    </p>
                  )}
                  {proj.achievements && (
                    <p className="text-gray-700 text-sm mt-1 whitespace-pre-line">
                      {proj.achievements}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 技能 */}
        {professionalSkills.length > 0 && (
          <div className="bg-white p-5 border border-gray-300">
            <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest mb-4 pb-2 border-b-2 border-gray-900">
              Skills
            </h2>
            <div className="flex flex-wrap gap-2">
              {professionalSkills.map((skill, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-gray-900 text-white text-xs font-mono"
                >
                  {skill}
                </span>
              ))}
            </div>
            {(skills.languages || skills.certificates) && (
              <div className="mt-4 pt-4 border-t border-gray-200 text-xs space-y-1 font-mono">
                {skills.languages && (
                  <p className="text-gray-600">
                    <span className="text-gray-900">LANG:</span>{' '}
                    {skills.languages}
                  </p>
                )}
                {skills.certificates && (
                  <p className="text-gray-600">
                    <span className="text-gray-900">CERT:</span>{' '}
                    {skills.certificates}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== 模板16: CleanWhite - 纯净白风 ====================

const CleanWhiteTemplate: React.FC<{ data: ResumeData }> = ({ data }) => {
  const { basicInfo, education, workExperience, projects, skills } = data;
  const professionalSkills = parseSkills(skills.professional);

  return (
    <div className="w-[210mm] min-h-[297mm] mx-auto bg-white shadow-lg print:shadow-none font-sans text-gray-600">
      {/* 极简头部 */}
      <div className="p-12 pb-8">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-5xl font-light text-gray-800 mb-2">
              {basicInfo.fullName}
            </h1>
            <p className="text-gray-400 text-lg">
              {basicInfo.jobTitle}
              {basicInfo.workYears && (
                <span className="ml-2 text-gray-300">
                  · {basicInfo.workYears}
                </span>
              )}
            </p>
          </div>
          {basicInfo.avatar && (
            <img
              src={basicInfo.avatar}
              alt={basicInfo.fullName}
              className="w-20 h-20 object-cover rounded-sm opacity-80"
            />
          )}
        </div>
        <div className="mt-8 pt-6 border-t border-gray-100 flex gap-8 text-sm text-gray-400">
          <span>{basicInfo.email}</span>
          <span className="text-gray-200">/</span>
          <span>{basicInfo.phone}</span>
          <span className="text-gray-200">/</span>
          <span>{basicInfo.location}</span>
        </div>
      </div>

      <div className="px-12 pb-12 space-y-10">
        {/* 个人优势 */}
        {basicInfo.advantages && (
          <section>
            <h2 className="text-xs text-gray-300 uppercase tracking-[0.3em] mb-6">
              Advantages
            </h2>
            <p className="text-gray-500 leading-loose max-w-3xl whitespace-pre-line">
              {basicInfo.advantages}
            </p>
          </section>
        )}

        {/* 个人简介 */}
        {basicInfo.summary && (
          <section>
            <p className="text-gray-500 leading-loose max-w-3xl">
              {basicInfo.summary}
            </p>
          </section>
        )}

        {/* 工作经历 */}
        {workExperience.length > 0 && (
          <section>
            <h2 className="text-xs text-gray-300 uppercase tracking-[0.3em] mb-6">
              Experience
            </h2>
            <div className="space-y-6">
              {workExperience.map((work) => (
                <div key={work.id}>
                  <div className="flex justify-between items-baseline mb-1">
                    <h3 className="text-lg text-gray-700">{work.company}</h3>
                    <span className="text-xs text-gray-300">
                      {formatDateRange(work.startDate, work.endDate)}
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm mb-2">{work.position}</p>
                  {work.description && (
                    <p className="text-gray-400 text-sm leading-relaxed whitespace-pre-line">
                      {work.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 项目经验 */}
        {projects.length > 0 && (
          <section>
            <h2 className="text-xs text-gray-300 uppercase tracking-[0.3em] mb-6">
              Projects
            </h2>
            <div className="space-y-4">
              {projects.map((proj) => (
                <div key={proj.id}>
                  <div className="flex justify-between items-baseline">
                    <h3 className="text-base text-gray-700">{proj.name}</h3>
                    <span className="text-xs text-gray-300">
                      {formatDateRange(proj.startDate, proj.endDate)}
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm">{proj.role}</p>
                  {proj.techStack && (
                    <p className="text-gray-300 text-xs mt-1">
                      {proj.techStack}
                    </p>
                  )}
                  {proj.achievements && (
                    <p className="text-gray-500 text-sm mt-1 whitespace-pre-line">
                      {proj.achievements}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 教育背景 */}
        {education.length > 0 && (
          <section>
            <h2 className="text-xs text-gray-300 uppercase tracking-[0.3em] mb-6">
              Education
            </h2>
            <div className="space-y-4">
              {education.map((edu) => (
                <div key={edu.id}>
                  <div className="flex justify-between items-baseline">
                    <h3 className="text-base text-gray-700">{edu.school}</h3>
                    <span className="text-xs text-gray-300">
                      {formatDateRange(edu.startDate, edu.endDate)}
                    </span>
                  </div>
                  <p className="text-gray-500 text-sm">
                    {edu.degree} · {edu.major}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 技能 */}
        {professionalSkills.length > 0 && (
          <section>
            <h2 className="text-xs text-gray-300 uppercase tracking-[0.3em] mb-6">
              Skills
            </h2>
            <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-gray-500">
              {professionalSkills.map((skill, index) => (
                <span key={index}>{skill}</span>
              ))}
            </div>
          </section>
        )}

        {/* 语言与证书 */}
        {(skills.languages || skills.certificates) && (
          <section>
            <h2 className="text-xs text-gray-300 uppercase tracking-[0.3em] mb-6">
              Credentials
            </h2>
            <div className="grid grid-cols-2 gap-6 text-sm text-gray-500">
              {skills.languages && (
                <div>
                  <span className="text-gray-300 text-xs block mb-1">
                    Languages
                  </span>
                  {skills.languages}
                </div>
              )}
              {skills.certificates && (
                <div>
                  <span className="text-gray-300 text-xs block mb-1">
                    Certificates
                  </span>
                  {skills.certificates}
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

// ==================== 导出所有模板 ====================

export const resumeTemplates = {
  elegant: {
    id: 'elegant',
    name: '优雅专业',
    description: '卡片式优雅设计，适合传统行业',
    component: ElegantTemplate,
  },
  even: {
    id: 'even',
    name: '极简现代',
    description: '扁平化现代设计，适合互联网/科技行业',
    component: EvenTemplate,
  },
  kendall: {
    id: 'kendall',
    name: '商务专业',
    description: '专业商务风格，适合金融/咨询行业',
    component: KendallTemplate,
  },
  caffeine: {
    id: 'caffeine',
    name: '创意大胆',
    description: '创意大胆设计，适合设计/创意行业',
    component: CaffeineTemplate,
  },
  gradientPro: {
    id: 'gradientPro',
    name: '渐变专业',
    description: '蓝紫渐变背景，现代专业感',
    component: GradientProTemplate,
  },
  magazine: {
    id: 'magazine',
    name: '杂志排版',
    description: '不对称布局，杂志风格',
    component: MagazineTemplate,
  },
  timeline: {
    id: 'timeline',
    name: '时间线',
    description: '中央垂直时间线设计',
    component: TimelineTemplate,
  },
  cards: {
    id: 'cards',
    name: '卡片堆叠',
    description: '独立卡片悬浮效果',
    component: CardsTemplate,
  },
  sidebarRight: {
    id: 'sidebarRight',
    name: '右侧边栏',
    description: '深色右侧边栏布局',
    component: SidebarRightTemplate,
  },
  minimalLine: {
    id: 'minimalLine',
    name: '极简线条',
    description: '细线条分隔，极简风格',
    component: MinimalLineTemplate,
  },
  colorBlock: {
    id: 'colorBlock',
    name: '色块分区',
    description: '几何色块装饰，活泼风格',
    component: ColorBlockTemplate,
  },
  typography: {
    id: 'typography',
    name: '字体排版',
    description: '超大号姓名，强调排版',
    component: TypographyTemplate,
  },
  photoHeader: {
    id: 'photoHeader',
    name: '照片头部',
    description: '全宽背景图头部',
    component: PhotoHeaderTemplate,
  },
  twoColumn: {
    id: 'twoColumn',
    name: '双栏紧凑',
    description: '高信息密度双栏布局',
    component: TwoColumnTemplate,
  },
  modernBox: {
    id: 'modernBox',
    name: '现代方框',
    description: '直角设计，工业感',
    component: ModernBoxTemplate,
  },
  cleanWhite: {
    id: 'cleanWhite',
    name: '纯净白',
    description: '纯白背景，灰色文字，极简',
    component: CleanWhiteTemplate,
  },
};

export type ResumeTemplateKey = keyof typeof resumeTemplates;

export const getTemplateComponent = (templateId: ResumeTemplateKey) => {
  return (
    resumeTemplates[templateId]?.component || resumeTemplates.elegant.component
  );
};

export const getTemplateList = () => {
  return Object.values(resumeTemplates).map(({ id, name, description }) => ({
    id,
    name,
    description,
  }));
};

export default resumeTemplates;
